'use client';

import { c as _c } from "react/compiler-runtime";
import { combine, FORGE_BASE, FORGE_EMOJI } from '@shared/all/domains/forge';
import { useEffect, useState } from 'react';
import { client } from '../../../utils/trpc';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
const BEST = bestKey('forge');
const SAVE_KEY = 'factiii:fun:forge:discovered';

/**
 * What the bench is showing. A union rather than separate `pending`/`flash`
 * states because these are mutually exclusive — you cannot be waiting on the
 * forge and displaying its answer at the same time.
 */
type Bench = {
  kind: 'idle';
} | {
  kind: 'pending';
  a: string;
  b: string;
} | {
  kind: 'result';
  name: string;
  isNew: boolean;
  discovered: boolean;
} | {
  kind: 'nothing';
} | {
  kind: 'unreachable';
};
const Chip = t0 => {
  const $ = _c(13);
  const {
    name,
    emoji,
    onClick,
    selected: t1,
    highlight: t2,
    disabled: t3
  } = t0;
  const selected = t1 === undefined ? false : t1;
  const highlight = t2 === undefined ? false : t2;
  const disabled = t3 === undefined ? false : t3;
  const classes = `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-all duration-200 ${highlight ? "border-primary bg-primary/15 text-textPrimary" : selected ? "border-ds-border-strong bg-background-light text-textPrimary" : "border-ds-border-standard bg-background-secondary text-textPrimary hover:-translate-y-0.5 hover:border-ds-border-hover hover:bg-background-light"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`;
  let t4;
  if ($[0] !== emoji) {
    t4 = <span aria-hidden="true">{emoji}</span>;
    $[0] = emoji;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== name || $[3] !== t4) {
    t5 = <>{t4}{name}</>;
    $[2] = name;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const content = t5;
  if (!onClick) {
    let t6;
    if ($[5] !== classes || $[6] !== content) {
      t6 = <span className={classes}>{content}</span>;
      $[5] = classes;
      $[6] = content;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  let t6;
  if ($[8] !== classes || $[9] !== content || $[10] !== disabled || $[11] !== onClick) {
    t6 = <button type="button" onClick={onClick} disabled={disabled} className={classes}>{content}</button>;
    $[8] = classes;
    $[9] = content;
    $[10] = disabled;
    $[11] = onClick;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const FactForge = () => {
  const [discovered, setDiscovered] = useState<string[]>([...FORGE_BASE]);
  const [selected, setSelected] = useState<string[]>([]);
  const [bench, setBench] = useState<Bench>({
    kind: 'idle'
  });
  /** Emoji for elements the server invented — the seed table only covers seeds. */
  const [emoji, setEmoji] = useState<Record<string, string>>({});
  const best = useLocalBest(BEST);
  const {
    record
  } = useGameTracking();
  const emojiFor = (name: string) => emoji[name] ?? FORGE_EMOJI[name] ?? '✨';

  /* Hydrate on mount rather than in a useState initialiser: localStorage does
   * not exist during SSR, so seeding state from it would mismatch on hydration. */
  useEffect(() => {
    let ignore = false;
    try {
      const saved = window.localStorage.getItem(SAVE_KEY);
      if (saved && !ignore) {
        const parsed: unknown = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const names = parsed.filter((item): item is string => typeof item === 'string');
          // Union with the base set so a truncated save can't strand the player.
          setDiscovered([...new Set([...FORGE_BASE, ...names])]);
        }
      }
    } catch {
      // Corrupt or unavailable storage just means starting fresh.
    }
    return () => {
      ignore = true;
    };
  }, []);
  const persist = (names_0: string[]) => {
    try {
      window.localStorage.setItem(SAVE_KEY, JSON.stringify(names_0));
    } catch {
      // Progress simply isn't kept if storage is full or blocked.
    }
  };

  /** Add a result to this player's own set. Returns whether it was new to them. */
  const addDiscovered = (result: string) => {
    if (discovered.includes(result)) return false;
    const next = [...discovered, result];
    setDiscovered(next);
    persist(next);
    record('forge', next.length);
    return true;
  };
  const pick = async (name_0: string) => {
    // First of the pair — just hold it.
    if (selected.length === 0) {
      setSelected([name_0]);
      setBench({
        kind: 'idle'
      });
      return;
    }
    if (bench.kind === 'pending') return;
    const [first] = selected;
    setSelected([]);

    /* Seed recipes resolve locally with no round trip. Only pairs outside the
     * hand-authored tree reach the server, which is where the model lives — so
     * the opening game is instant and offline. */
    const local = combine(first, name_0);
    if (local) {
      setBench({
        kind: 'result',
        name: local,
        isNew: addDiscovered(local),
        discovered: false
      });
      return;
    }
    setBench({
      kind: 'pending',
      a: first,
      b: name_0
    });
    const [error, data] = await client.arcade.combine.mutate({
      a: first,
      b: name_0
    }, {
      context: {
        silenceErrors: true
      }
    });

    /* A failed call must not invent a result — but it must not claim "nothing
     * happens" either. Every valid pair now resolves to something, so that
     * message would be a lie about the game rather than a report of a fault. */
    if (error || !data) {
      setBench({
        kind: 'unreachable'
      });
      return;
    }
    setEmoji(current => ({
      ...current,
      [data.name]: data.emoji
    }));
    setBench({
      kind: 'result',
      name: data.name,
      isNew: addDiscovered(data.name),
      // Only the server can say this element never existed anywhere before.
      discovered: data.discovered
    });
  };
  const reset = () => {
    const base = [...FORGE_BASE];
    setDiscovered(base);
    setSelected([]);
    setBench({
      kind: 'idle'
    });
    persist(base);
  };
  const held = selected[0];
  const busy = bench.kind === 'pending';
  return <div>
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-sm text-textSecondary">
          <span className="font-emphasis text-textPrimary">
            {discovered.length}
          </span>
          {' discovered'}
        </p>
        <button type="button" onClick={reset} className="text-sm text-textGray underline underline-offset-2 transition-colors hover:text-textPrimary">
          Start over
        </button>
      </div>

      {/* The bench: what you are holding, and what came out of it. */}
      <div aria-live="polite" aria-busy={busy} className="mb-6 flex min-h-[76px] flex-wrap items-center justify-center gap-3 rounded-2xl border border-ds-border-standard bg-background-secondary p-4">
        {held && !busy && <>
            <Chip name={held} emoji={emojiFor(held)} selected />
            <span className="text-textGray">+</span>
            <span className="text-textGray">?</span>
          </>}

        {busy && <>
            <Chip name={bench.a} emoji={emojiFor(bench.a)} selected />
            <span className="text-textGray">+</span>
            <Chip name={bench.b} emoji={emojiFor(bench.b)} selected />
            <span className="flex items-center gap-2 text-sm text-textSecondary">
              <span className="loader" />
              Forging something new…
            </span>
          </>}

        {!held && bench.kind === 'result' && <>
            <Chip name={bench.name} emoji={emojiFor(bench.name)} highlight={bench.isNew} />
            {bench.discovered ? <span className="text-sm font-emphasis text-primary">
                🏆 First ever discovery — credited to you
              </span> : bench.isNew && <span className="text-sm font-emphasis text-primary">New!</span>}
          </>}

        {!held && bench.kind === 'nothing' && <span className="text-sm text-textGray">
            Nothing happens. Try a different pair.
          </span>}

        {!held && bench.kind === 'unreachable' && <span className="text-sm text-textGray">
            The forge is unreachable right now. Your discoveries are safe — try
            that pair again in a moment.
          </span>}

        {!held && bench.kind === 'idle' && <span className="text-sm text-textGray">
            Click one element, then another, to fuse them.
          </span>}
      </div>

      <div className="flex max-h-[46vh] flex-wrap content-start gap-2 overflow-y-auto rounded-2xl border border-ds-border-subtle p-3">
        {discovered.map(name_1 => <Chip key={name_1} name={name_1} emoji={emojiFor(name_1)} disabled={busy} onClick={() => {
        void pick(name_1);
      }} />)}
      </div>

      {best !== null && <p className="mt-4 text-sm text-textGray">
          Most ever discovered: {best}
        </p>}
    </div>;
};
export default FactForge;